import React, { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import { BASENAME, SHOWING_HOTEL_LIMIT } from '../../../constants';
import Loading from '../../atoms/Loading';
import LineTitleGray from '../../atoms/titles/LineTitleGray';
import SubscriptionTable from '../../organisms/SubscriptionTable';
import LinkButton from '../../atoms/button/LinkButton';
import SubscriptionPrice from '../../molecules/SubscriptionPrice';
import NoteEntryConfirmation from '../../molecules/NoteEntryConfirmation';
import DataUpdateTimeDisplay from '../../molecules/DataUpdateTimeDisplay';
import SubscriptionPlanContents from '../../organisms/SubscriptionPlanContents';
import { LAYOUT_TYPES, SHOWING_HOTEL_LIMI, PLAN_OPTION_CHARGE_KIND, UNEXPECTED_ERROR_MESSAGE } from '../../../constants';
import { setCurrentStep, setLayoutType, setErrorMessages } from '../../../modules/app';
import {
  fetchHotels,
} from '../../../modules/hotel';
import { makeHotelsInput, makeCreateSessionInput } from '../../../utils/input';
import { useSearchFlightSync } from '../../../modules/hooks/searchFlight';
import { useWindowSize } from '../../../utils/windowSize';
import { scrollToTop } from '../../../utils/scroll';
import { getPlanFromPlansByDate } from '../../../utils/plans';
import { getPlanOptionFromPlanOptionsByDate } from '../../../utils/planOptions';
import { getCustomerCode } from '../../../utils/cookie';
import { createSession } from '../../../modules/session';
import { usePrice } from '../../../utils/price';
import { useProgress } from '../../../modules/hooks/progress';
import PlanningInfo from '../../molecules/PlanningInfo';
import { FData } from '../../Head';
import { getSelectedPlanOptionsState, getSessionId } from '../../../utils/localStorage';
import { sessionKey } from '../../../modules/app';
import environments from '../../../environments';
import {
  makeAdditionalParamsPlanDetail,
  makeQueryStringsWithSessionKey,
} from '../../../utils/queryParams';
import { getIsAna2 } from '../../../utils/Ana2';
const selector = (state) => ({ planDetail: state.planDetail });

const EntryConfirmation = () => {
  const dispatch = useDispatch();
  const [ isPCView, setIsPCView ] = useState(false);
  const [ editingFlights, setEditingFlights ] = useState(false);
  const [ editingAccommodation, setEditingAccommodation ] = useState(false);
  const [ maxStayNights, setMaxStayNights ] = useState(undefined);
  const [ planStartDate, setPlanStartDate ] = useState(undefined);
  const [ planIndex, setPlanIndex ] = useState(undefined);
  const [ fetching, setFetching ] = useState(false);
  const [ isIntersectingNote, setIsIntersectingNote ] = useState(false);
  const [ isIntersectingBottomAction, setIsIntersectingBottomAction ] = useState(false);
  const [ questionAndAnswers, setQuestionAndAnswers] = useState([]);
  const showFloatingBottom = isIntersectingNote && !isIntersectingBottomAction;
  const noteRef = useRef();
  const bottomActionRef = useRef();

  useEffect(() => {
    if (noteRef.current) {
      const intersectionCallback = (entries) => {
        entries.forEach(entry => {
          setIsIntersectingNote(entry.isIntersecting);
        });
      };
      const intersectionObserverNote = new IntersectionObserver(intersectionCallback);
      intersectionObserverNote.observe(noteRef.current);
    }
  }, [noteRef.current]);

  useEffect(() => {
    if (bottomActionRef.current) {
      const intersectionCallback = (entries) => {
        entries.forEach(entry => {
          setIsIntersectingBottomAction(entry.isIntersecting);
        });
      };
      const intersectionObserverBottom = new IntersectionObserver(intersectionCallback);
      intersectionObserverBottom.observe(bottomActionRef.current);
    }
  }, [bottomActionRef.current]);

  const windowSize = useWindowSize();

  const toggleEditingFlights = useCallback(
    () => {
      setEditingFlights(!editingFlights);
    },
    [ editingFlights ],
  );

  const toggleEditingAccommodation = useCallback(
    () => {
      setEditingAccommodation(!editingAccommodation);
    },
    [ editingAccommodation ],
  );

  const finishEditing = useCallback(
    () => {
      setEditingFlights(false);
      setEditingAccommodation(false);
    },
    [],
  );

  useEffect(() => {
    scrollToTop(false);
  }, [ editingFlights, editingAccommodation ]);

  useEffect(() => {
    dispatch(
      setLayoutType(LAYOUT_TYPES.step)
    );

    dispatch(
      setCurrentStep(1)
    );

    return () => {
      dispatch(
        setLayoutType(LAYOUT_TYPES.default)
      );
    };
  }, []);

  useEffect(() => {
    if (editingFlights || editingAccommodation) {
      return;
    }

    const pcView = windowSize.width > 768;
    setIsPCView(pcView);
  }, [windowSize]);

  const { flight, hotel } = useSearchFlightSync(true, true, true);

  const { planDetail } = useSelector(selector);
  const planOptionsFromStorage = getSelectedPlanOptionsState(sessionKey);
  const planOptions = Array.isArray(planOptionsFromStorage) ? planOptionsFromStorage : [];

  const { plans, fetching: fetchingPlanDetail } = planDetail;
  const {
    fetchingOutbound,
    fetchingInbound,
    searchedFlightsInput,
    selectedOutboundFlight,
    selectedInboundFlight,
    selectedOutboundDate,
    selectedInboundDate,
    outboundFlights,
    inboundFlights,
  } = flight;
  const fetchingFlight = fetchingOutbound || fetchingInbound;
  const { fetching: fetchingHotel, searchedHotelsInput, hotelsInput } = hotel;

  const tripStartDate = moment(selectedOutboundDate);

  useEffect(() => {
    if (editingAccommodation) {
      const formatedTripStartDate = tripStartDate.clone().format('YYYY-MM-DD');
      
      const _hotelsInput = planIndex === 1 ? {
        ...hotelsInput,
        hotelCode: plans.find(p => p.startDate === planStartDate).plan.hotel.id,
      } : {
        ...hotelsInput,
        provider: plans.find(p => p.startDate === formatedTripStartDate).plan.provider,
        excludeLandingFlag: 1, // プラン追加時は着地ありプランは除外する
        searchCondition: [], //  プラン追加時は着地用こだわり条件はクリア
        hotelCode: '', // プラン追加時はhotelCodeをクリア
        groupId: '',// プラン追加時はgroupIdをクリア
        itemCD: '', // プラン追加時はITEM_CDをクリア
        planPerHotel: "20", // プラン追加時はplanPerHotelを20に設定
      };

      dispatch(
        fetchHotels(
          makeHotelsInput(_hotelsInput, searchedFlightsInput, planStartDate),
          SHOWING_HOTEL_LIMIT,
          1
        )
      );
    }
  }, [editingAccommodation]);

    // planOptionsが配列で来たタイミングで実行
    useEffect(() => {
      if (Array.isArray(planOptions) && planOptions.length > 0) {
        const facilityQuestions = planOptions.map((option) => ({
          itineraryIndex: option.itineraryIndex,
          goodsCode: option.goods.goodsCode,
          facilityQuestions: option.goods.facilityQuestions
        }));
        setQuestionAndAnswers(facilityQuestions);
      }
    }, [planOptions && planOptions.length, planOptions && planOptions[0] && planOptions[0].goods && planOptions[0].goods.goodsCode]);

  // 価格計算
  const { totalPriceIncludePlanOption, peoplePrice } = usePrice(
    searchedFlightsInput && searchedFlightsInput.people,
    selectedOutboundFlight,
    selectedInboundFlight,
    plans && plans.map(p => p.plan) || [],
    selectedOutboundDate,
    planOptions
  );

  if (!searchedFlightsInput || !searchedHotelsInput) {
    return null;
  }
  
  let title = '申込み内容確認';
  if (editingFlights) {
    title = '航空便選択';
  }
  if (editingAccommodation) {
    const planStartDateDiplay = moment(planStartDate).format('YYYY年M月D日(ddd)');
    title = `宿泊内容変更 ${planIndex}日目 ${planStartDateDiplay}`;
  }

  const tripEndDate = moment(selectedInboundDate);
  const tripTotalDays = tripEndDate.diff(tripStartDate, 'days') + 1;

  let planEndDate = undefined;

  const schedule = new Array(tripTotalDays).fill(undefined).map((_, i) => {
    const date = tripStartDate.clone().add(i, 'days');

    const plan = getPlanFromPlansByDate(plans, date);

    if (plan) {
      planEndDate = date.clone().format('YYYY-MM-DD');
    }

    const planOptionsByDate = getPlanOptionFromPlanOptionsByDate(planOptions, date);

    return {
      date,
      plan,
      planOptions: planOptionsByDate,
    };
  });

  const handleClickButton = async () => {

    // 質問回答必須時のバリデーション
    const haveQuestionAndAnswers = questionAndAnswers.filter(qa => qa.facilityQuestions);
    
    // 必須質問のバリデーション
    const validationErrors = [];
    haveQuestionAndAnswers.forEach((qa) => {
      if (qa.facilityQuestions) {
        qa.facilityQuestions.forEach((question, index) => {
          if (question.required === 1) {
            // answerが空文字、null、undefined、またはfalseの場合はエラー
            if (!question.answer || question.answer === '' || question.answer === false) {
              validationErrors.push({
                goodsCode: qa.goodsCode,
                questionIndex: index,
                questionContent: question.content,
                elementId: `${qa.itineraryIndex}-${qa.goodsCode}-q${index}`,
                errorMessage: '施設からの質問が未入力です。すべての必須項目に回答してください。',
              });
            }
          }
          // 回答が1200文字を超えている場合はエラー
          if(question.answer && question.answer.length > 1200) {
            validationErrors.push({
              goodsCode: qa.goodsCode,
              questionIndex: index,
              questionContent: question.content,
              elementId: `${qa.itineraryIndex}-${qa.goodsCode}-q${index}`,
              errorMessage: '回答が1200文字を超えています。1200文字以内で回答してください。',
            });
          }
        });
      }
    });

    // バリデーションエラーがある場合は処理を停止
    if (validationErrors.length > 0) {
      validationErrors.map((error) => {
        const element = document.getElementById(error.elementId);
        if (element) {
          element.classList.add('hasError');
        }
      });
      // エラーメッセージを表示（アラートやモーダルなど）
      alert(validationErrors.map(error => error.errorMessage).join('\n'));
      return;
    }

    // planOptionsのfacilityQuestionsをquestionAndAnswersで更新
    const updatedPlanOptions = planOptions.map((option, index) => {
      const questionAnswer = questionAndAnswers.find(qa => qa.itineraryIndex === option.itineraryIndex);
      let chargeKindsInputs;
      if (option.goods.goodsDetailInfo.useUnit === 'group') {
        // useUnitが'group'の場合は、AD（大人）1名にだけ値をセット
        const adCount = option.peopleCounts['AD'] || 0;
        if (adCount > 0) {
          chargeKindsInputs = [{
            chargeKind: 'AD',
            sort: 0,
            sex: null,
          }];
        } else {
          chargeKindsInputs = [];
        }
      } else {
        // useUnitが'group'でない場合は、人員分だけ値をセット
        chargeKindsInputs = PLAN_OPTION_CHARGE_KIND
        .filter((kind) => option.peopleCounts[kind.id] > 0)
        .flatMap((kind) => 
          Array.from({ length: option.peopleCounts[kind.id] }, () => ({
            chargeKind: kind.id,
          }))
        )
        .map((item, globalIndex) => ({
          ...item,
          sort: globalIndex,
          sex: null,
        }));
      }

      if (questionAnswer) {
        return {
          ...option,
          goods: {
            ...option.goods,
            facilityQuestions: questionAnswer.facilityQuestions,
            chargeKinds: chargeKindsInputs
          }
        };
      }
      return option;
    });

    setFetching(true);

    const customerCode = getCustomerCode();

    // sessionレコードcreateのmutationを実行
    const sessionInput = makeCreateSessionInput(
      selectedOutboundFlight, selectedInboundFlight,
      selectedOutboundDate, selectedInboundDate,
      plans, searchedFlightsInput, searchedHotelsInput, 
      updatedPlanOptions, customerCode
    );
    try {
      // si に検索用 k を同居させ、WESTER復帰後も planDetail.plans を参照できるようにする
      const createdSessionId = await createSession(sessionInput, sessionKey);
      setFetching(false);

      // cookie[W_CustomersCode]を取得
      // W_CustomersCode=W2の場合は、WESTERログイン画面へリダイレクト
      const wCustomersCodeString = document.cookie.split('; ').find(row => row.startsWith('W_CustomersCode='));
      const wCustomersCode = wCustomersCodeString ? wCustomersCodeString.split('=')[1] : null;
      const sessionId = createdSessionId || getSessionId();
      const redirectUrl = (wCustomersCode && wCustomersCode === 'W2') ? `${environments.API_BASE_URI}/westerLogin/redirect?s=${sessionId}` : `${BASENAME}/login`;

      window.open(redirectUrl, '_self');
    } catch (error) {
      setFetching(false);
      let errorCode = [];
      if (error && error.graphQLErrors && error.graphQLErrors.length) {
        errorCode = error.graphQLErrors.map((e) => (e.debugMessage || e.message)).filter(Boolean);
      }

      if(errorCode[0] === '999'){
        window.open(`${BASENAME}/search?error=999&errorMsg=${UNEXPECTED_ERROR_MESSAGE}`, '_self');
      }
    }
  };

  const showSaveFlightsButton = !!(outboundFlights && outboundFlights.length && inboundFlights && inboundFlights.length);

  const { progressRate } = useProgress(fetchingOutbound, fetchingInbound, fetchingPlanDetail || fetchingHotel);

  const firstPlan = plans && plans[0] && plans[0].plan;
  const { id: planId, roomType: roomType } = firstPlan || {};
  const roomTypeId = roomType ? roomType.id : null;

  // 着地の質問回答を更新
  const handleQuestionAnswerChange = (itineraryIndex, questionIndex, value) => {
    setQuestionAndAnswers(prev => {
      const newAnswers = [...prev];
      const optionIndex = newAnswers.findIndex(option => option.itineraryIndex === itineraryIndex);
      if (optionIndex !== -1) {
        newAnswers[optionIndex] = {
          ...newAnswers[optionIndex],
          facilityQuestions: newAnswers[optionIndex].facilityQuestions.map((question, i) => 
            i === questionIndex ? { ...question, answer: value } : question
          )
        };
      }
      return newAnswers;
    });
  };
  
  // タビサキMenuを変更する
  const handlePlanOptionChange = () => {
    const planDetailParams = makeAdditionalParamsPlanDetail(planId, roomTypeId);
    const searchConfirm = makeQueryStringsWithSessionKey(planDetailParams, sessionKey);
    window.open(`${BASENAME}/plan_option?${searchConfirm}`, '_self');
  };

  const isAna2 = getIsAna2(selectedOutboundFlight.airline, selectedOutboundDate);

  return (
    <div className="pageConfirmation">
      <FData action="checkout" flightState={flight} plnaDetailState={planDetail} />
      <Loading
        show={fetchingFlight || fetchingHotel || fetchingPlanDetail || fetching}
        progressRate={progressRate}
        hideProgressBar={fetching}
      />
      <div className="subscriptionCheckWrapper">
        <div className="subscriptionCheckWrapperTitle">
          <LineTitleGray
            size="lg"
            linkTitle={editingAccommodation ? '変更せずに戻る' : undefined}
            handleLinkClick={finishEditing}
          >
            {title}
          </LineTitleGray>
        </div>
        {!editingFlights && !editingAccommodation && <div className="subscriptionCheckWrapperDescription">旅行行程や時間を確認の上、お申し込み手続きへお進みください。</div>}
        <div className="table">
          {isPCView ? (
            <SubscriptionTable
              editingFlights={editingFlights}
              editingAccommodation={editingAccommodation}
              toggleEditingFlights={toggleEditingFlights}
              toggleEditingAccommodation={toggleEditingAccommodation}
              tripTotalDays={tripTotalDays}
              schedule={schedule}
              plans={plans}
              maxStayNights={maxStayNights}
              setMaxStayNights={setMaxStayNights}
              planStartDate={planStartDate}
              setPlanStartDate={setPlanStartDate}
              planEndDate={planEndDate}
              setPlanIndex={setPlanIndex}
              planIndex={planIndex}
              planOptions={planOptions}
              questionAndAnswers={questionAndAnswers}
              onQuestionAnswerChange={handleQuestionAnswerChange}
            />
          ) : (
            <SubscriptionPlanContents
              editingAccommodation={editingAccommodation}
              toggleEditingFlights={toggleEditingFlights}
              toggleEditingAccommodation={toggleEditingAccommodation}
              tripTotalDays={tripTotalDays}
              schedule={schedule}
              plans={plans}
              maxStayNights={maxStayNights}
              setMaxStayNights={setMaxStayNights}
              planStartDate={planStartDate}
              setPlanStartDate={setPlanStartDate}
              planEndDate={planEndDate}
              planIndex={planIndex}
              setPlanIndex={setPlanIndex}
              questionAndAnswers={questionAndAnswers}
              onQuestionAnswerChange={handleQuestionAnswerChange}
            />
          )}

        {(planOptions.length > 0 && (!editingFlights && !editingAccommodation)) && (
          <div className="planOptionChangeButton">
            <button 
            className="button primary"
            onClick={handlePlanOptionChange}>タビサキMenuを変更する</button>
          </div>
        )}

          {!(editingFlights || editingAccommodation) && (
            <div style={{ marginTop: 32 }}>
              <SubscriptionPrice
                priceTotal={totalPriceIncludePlanOption}
                peoplePrice={peoplePrice}
                planOptions={planOptions}
              />
            </div>
          )}
          {!editingAccommodation && (
            <React.Fragment>
              <div className="subscriptionCheckActions">
                <div className="linkActions">
                  {!(editingFlights) ? (
                    <div className="linkMypage subscriptionCheckLinkWrapper">
                      <DataUpdateTimeDisplay
                        showUpdateNote
                        className="subscriptionCheckNote"
                      />
                      {firstPlan && (firstPlan.chargesRemarks || firstPlan.chargesInfantRemarks) && (
                        <div className="planRemarks">
                          {firstPlan.chargesRemarks && <p dangerouslySetInnerHTML={{ __html: firstPlan.chargesRemarks }} />}
                          {firstPlan.chargesInfantRemarks && (
                            <p dangerouslySetInnerHTML={{ __html: firstPlan.chargesInfantRemarks }} style={{ marginTop: 8 }} />
                          )}
                        </div>
                      )}
                      <LinkButton buttonType="cta" onClick={handleClickButton}>申込み手続きへ</LinkButton>
                    </div>
                  ) : (
                    <React.Fragment>
                      <div className="linkClose">
                        <LinkButton buttonType="navigate" onClick={finishEditing}>戻る</LinkButton>
                      </div>
                      {showSaveFlightsButton && (
                        <div className="linkMypage">
                          <LinkButton buttonType="cta" onClick={finishEditing}>内容確認へ</LinkButton>
                        </div>
                      )}
                    </React.Fragment>
                  )}
                </div>
              </div>
              {!editingFlights && (
                <div className="attentionText">【必ず「申し込み注意事項」をお読みいただいた上、お進みください】</div>
              )}
            </React.Fragment>
          )}
        </div>
      </div>
      {!(editingFlights || editingAccommodation) && (
        <React.Fragment>
          <NoteEntryConfirmation noteRef={noteRef} />

          <div style={{ marginTop: 60 }}>
            <SubscriptionPrice
              priceTotal={totalPriceIncludePlanOption}
              peoplePrice={peoplePrice}
              planOptions={planOptions}
            />
          </div>
          <div className="subscriptionCheckActions">
            <div className="linkActions">
              <div ref={bottomActionRef} className="linkMypage subscriptionCheckLinkWrapper">
                <DataUpdateTimeDisplay
                  showUpdateNote
                  className="subscriptionCheckNote"
                />
                {firstPlan && (firstPlan.chargesRemarks || firstPlan.chargesInfantRemarks) && (
                  <div className="planRemarks">
                    {firstPlan.chargesRemarks && <p dangerouslySetInnerHTML={{ __html: firstPlan.chargesRemarks }} />}
                    {firstPlan.chargesInfantRemarks && (
                      <p dangerouslySetInnerHTML={{ __html: firstPlan.chargesInfantRemarks }} style={{ marginTop: 8 }} />
                    )}
                  </div>
                )}
                <LinkButton buttonType="cta" onClick={handleClickButton} >申込み手続きへ</LinkButton>
              </div>
            </div>
          </div>
          <div className="attentionText">【必ず「申し込み注意事項」をお読みいただいた上、お進みください】</div>
          {(schedule && schedule.length && schedule[0].plan) && (
            <PlanningInfo className="mt-40" info={schedule[0].plan.planningImplementation} />
          )}
        </React.Fragment>
      )}
      {showFloatingBottom && (
        <div className="floatingBottom">
          <div className="linkActions">
            <LinkButton buttonType="cta" onClick={handleClickButton}>申込み手続きへ</LinkButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default EntryConfirmation;
